/**
 * HOW A SCHEMA FIELD IS CAPTIONED — one spelling, every surface.
 *
 * Reported by George Karakitsios, 2026-08-27, against the Data area's record detail: *"Make
 * the Key*, Cms* etc in bold, same size and their descriptions smaller font and maybe show only
 * first 2 lines, the rest could be revealed"*. Both halves are presentation — nothing here
 * reads the schema, and nothing here needs the schema to grow a key.
 *
 * It lives in its own module rather than inline in `DataSetForm` because the Data area is not
 * the only place a field's name and description are shown. Anything else that lists schema
 * fields imports `FIELD_LABEL_CLASS` and `<FieldDescription>` instead of re-deciding the type
 * scale, so the two surfaces cannot drift apart the way they would if each spelled its own
 * Tailwind classes.
 *
 * MEASURED, NOT GUESSED. Whether prose runs past two lines depends on the container's width,
 * the glyphs, where the words break and the browser's zoom — so the toggle is driven by the
 * paragraph's own `scrollHeight` against its `clientHeight` while clamped, re-checked through
 * a `ResizeObserver` when the panel resizes. The character count below is only the FALLBACK,
 * for the first paint and for an environment that reports no layout at all (jsdom, or a
 * collapsed ancestor): there, a guess that is usually right beats a measurement of zero, which
 * would decide that nothing anywhere overflows.
 */

import { useId, useLayoutEffect, useRef, useState } from 'react'

/** The field's name. Bold, and the same size wherever it appears — the "same size" half of
 *  the report, held as one constant so no surface can quietly pick its own scale. */
export const FIELD_LABEL_CLASS = 'text-sm font-semibold text-gray-900'

/** Its description: a step down in size and weight, so the name is what the eye lands on. */
export const FIELD_DESCRIPTION_CLASS = 'text-xs text-gray-500 leading-snug'

/**
 * The first-paint estimate of "longer than two lines", used until layout can be measured.
 *
 * Roughly one and a half lines at `text-xs` across the detail panel, so ordinary one-line help
 * ("The property slug") is not given a toggle it does not need, while the ADR paragraphs that
 * prompted the report get one immediately rather than one frame late.
 */
export const DESCRIPTION_CLAMP_MIN_CHARS = 160

const estimatesOverflow = (text: string | undefined) =>
  (text?.length ?? 0) > DESCRIPTION_CLAMP_MIN_CHARS

export function FieldDescription(props: {
  text: string | undefined
  /** Rendered as the paragraph's `data-testid`; the toggle takes `${testId}-toggle`. */
  testId: string
  /** The field's name, so the toggle's accessible name says WHICH description it opens —
   *  every field on the record otherwise offers a control called "Show more". */
  label?: string
}) {
  const { text, testId, label } = props
  const [open, setOpen] = useState(false)
  const [overflowing, setOverflowing] = useState(() => estimatesOverflow(text))
  const paragraph = useRef<HTMLParagraphElement>(null)
  const descriptionId = useId()

  useLayoutEffect(() => {
    const node = paragraph.current
    // Measure only while CLAMPED: expanded, the paragraph is exactly as tall as its content,
    // so measuring would conclude it does not overflow and take away the control that is
    // holding it open.
    if (!node || open) return
    const measure = () => {
      // No layout to read (jsdom, `display: none`, an unattached tree). Every dimension is 0
      // and `0 > 0` would silently retire every toggle, so fall back to the estimate — and
      // RE-derive it rather than keeping what the last text produced, or a field whose
      // description shortens keeps a toggle that now reveals nothing.
      if (node.clientHeight === 0) return setOverflowing(estimatesOverflow(text))
      setOverflowing(node.scrollHeight > node.clientHeight + 1)
    }
    measure()
    // jsdom has no ResizeObserver and this project shims it per test file rather than
    // globally, so its absence is normal rather than exceptional.
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [text, open])

  if (!text) return null

  return (
    <>
      {/* Clamped whenever it is closed, including when it is short enough that the clamp does
          nothing — that is what makes the height measurable at all. The full text is ALWAYS in
          the DOM: the clamp is CSS, so collapsed prose stays selectable, copyable and reachable
          by the browser's own find, which "show the first two lines" asked for and "render the
          first two lines" would have taken away. */}
      <p
        ref={paragraph}
        id={descriptionId}
        data-testid={testId}
        // `print:line-clamp-none` because paper has no toggle. The clamp is `overflow:
        // hidden`, so without this a printed record silently loses the tail of every long
        // description — and prints a "Show more" nobody can click.
        className={[FIELD_DESCRIPTION_CLASS, open ? '' : 'line-clamp-2', 'print:line-clamp-none']
          .filter(Boolean).join(' ')}
      >
        {text}
      </p>
      {overflowing && (
        <button
          type="button"
          data-testid={`${testId}-toggle`}
          aria-expanded={open}
          aria-controls={descriptionId}
          aria-label={label
            ? `${open ? 'Show less' : 'Show more'} of the ${label} description`
            : undefined}
          onClick={() => setOpen(!open)}
          // `block`, not inline: a field's controls below it are inline-flex, so an inline
          // toggle joins their line and the field reads "Show more ☐ Not yet known ☑ True".
          className="block text-[11px] text-indigo-600 hover:text-indigo-800 hover:underline print:hidden"
        >
          {open ? 'Show less' : 'Show more'}
        </button>
      )}
    </>
  )
}
